import * as React from 'react'
import { ArrowSquareOut } from '@phosphor-icons/react'
import { Button } from 'src/design-system'
import { Editor } from 'components/atoms/editor'
import { CurrentRatingScalesModal } from './current-rating-scales-modal'
import { hasVisibleText, QuestionType, RatingScaleLabel, Team } from './types'

// green-600 accent matches the existing settings forms (checkin-settings-form).
const RADIO_ACCENT = { accentColor: '#16A34A' }
// Dark navy accent for the "Teams applied" checkboxes, per Figma.
const CHECKBOX_ACCENT = { accentColor: '#1E1B4B' }

function RadioOption({
  value,
  label,
  checked,
  onChange,
}: {
  value: QuestionType
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label className="flex-1 p-4 bg-white rounded-lg border border-gray-200 flex items-center gap-4 cursor-pointer">
      <input
        type="radio"
        name="question_type"
        value={value}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 shrink-0 cursor-pointer"
        style={RADIO_ACCENT}
      />
      <span className="font-talent text-base font-medium text-indigo-950 leading-5">
        {label}
      </span>
    </label>
  )
}

// "Teams applied" multi-select shown only on the Team specific tab. Lists every
// team in the org across two columns (matching Figma).
function TeamsApplied({
  teams,
  selectedTeamIds,
  onToggle,
}: {
  teams: Team[]
  selectedTeamIds: number[]
  onToggle: (teamId: number) => void
}) {
  const mid = Math.ceil(teams.length / 2)
  const columns = [
    ['left', teams.slice(0, mid)],
    ['right', teams.slice(mid)],
  ] as const

  return (
    <div className="flex flex-col gap-2">
      <span className="font-normal text-[15px] text-gray-500 leading-4">
        Teams applied
      </span>
      <div className="p-5 bg-gray-50 rounded-lg flex items-start gap-4">
        {columns.map(([columnKey, column]) => (
          <div key={columnKey} className="flex-1 flex flex-col gap-1">
            {column.map((team) => (
              <label
                key={team.id}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedTeamIds.includes(team.id)}
                  onChange={() => onToggle(team.id)}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded-sm"
                  style={CHECKBOX_ACCENT}
                />
                <span className="font-talent text-base font-normal text-indigo-950 leading-5">
                  {team.name}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export function QuestionCreationCard({
  ratingScaleLabels,
  orgSlug,
  onCreate,
  onCancel,
  teams,
  initialTeamIds = [],
  onViewRatingScales,
}: {
  ratingScaleLabels: RatingScaleLabel[]
  orgSlug: string
  onCreate: (input: {
    body: string
    questionType: QuestionType
    teamIds: number[]
  }) => Promise<void>
  /** Closes the creation card without saving. */
  onCancel: () => void
  /** When provided, renders the "Teams applied" multi-select (Team specific tab). */
  teams?: Team[]
  /** Teams pre-checked when the card opens (e.g. the team currently being viewed). */
  initialTeamIds?: number[]
  /**
   * Overrides the "View current rating scales" action. By default the card opens
   * its own stacked CurrentRatingScalesModal (from `ratingScaleLabels`); when this
   * is provided it is called instead — e.g. the check-in wizard uses it to open a
   * full-screen replace-style modal rather than stacking one over the wizard.
   */
  onViewRatingScales?: () => void
}) {
  const [body, setBody] = React.useState('')
  const [questionType, setQuestionType] =
    React.useState<QuestionType>('open_dialogue')
  const [selectedTeamIds, setSelectedTeamIds] =
    React.useState<number[]>(initialTeamIds)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showScales, setShowScales] = React.useState(false)
  // Remounts the rich-text editor to clear it after a successful create.
  const [editorKey, setEditorKey] = React.useState(0)

  // Consumers close the card by unmounting it once onCreate resolves, so the
  // post-await state resets below would land on an unmounted component. Track
  // mount status and skip them when the card is already gone.
  const mountedRef = React.useRef(true)
  React.useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const showTeams = teams !== undefined

  const toggleTeam = (teamId: number) => {
    const next = selectedTeamIds.includes(teamId)
      ? selectedTeamIds.filter((id) => id !== teamId)
      : [...selectedTeamIds, teamId]
    setSelectedTeamIds(next)
    // The team-selection error only clears on the next Create click otherwise
    // (it's set imperatively in handleCreate, not derived) — clear it as soon
    // as the team that resolves it is actually checked, matching how the body
    // field's error already clears reactively in the Editor's onChange below.
    if (error === 'Select at least one team.' && next.length > 0) {
      setError(null)
    }
  }

  const handleCreate = async () => {
    if (!hasVisibleText(body)) {
      setError('Question text is required.')
      return
    }
    if (showTeams && selectedTeamIds.length === 0) {
      setError('Select at least one team.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onCreate({
        body: body.trim(),
        questionType,
        teamIds: showTeams ? selectedTeamIds : [],
      })
      if (!mountedRef.current) return
      // Clear the field so the card is ready for the next question.
      setBody('')
      setEditorKey((key) => key + 1)
    } catch (err: unknown) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Something went wrong.')
      }
    } finally {
      if (mountedRef.current) setSaving(false)
    }
  }

  return (
    <>
      {showScales && (
        <CurrentRatingScalesModal
          labels={ratingScaleLabels}
          onClose={() => setShowScales(false)}
        />
      )}

      <div className="p-4 bg-white rounded-lg border border-gray-200 flex flex-col gap-4">
        {/* Question field */}
        <div className="flex flex-col gap-2">
          <label className="font-normal text-[15px] text-gray-500 leading-4">
            Question
          </label>
          <div className="rounded-sm bg-gray-100 p-2">
            <Editor
              key={editorKey}
              toolbar="static"
              placeholder="Type your question…"
              rows={3}
              onChange={(html) => {
                const value = html === '<p></p>' ? '' : html
                setBody(value)
                if (error && hasVisibleText(value)) setError(null)
              }}
            />
          </div>
          {error && (
            <p className="m-0 font-talent text-red-500 text-xs">{error}</p>
          )}
        </div>

        {/* Question type */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <span className="font-normal text-[15px] text-gray-500 leading-4">
              Question Type
            </span>
            <button
              type="button"
              className="font-talent text-base font-normal text-indigo-950 leading-4 hover:underline"
              onClick={() =>
                onViewRatingScales ? onViewRatingScales() : setShowScales(true)
              }
            >
              View current rating scales
            </button>
          </div>

          <div className="flex items-center gap-2">
            <RadioOption
              value="open_dialogue"
              label="Open-dialogue"
              checked={questionType === 'open_dialogue'}
              onChange={() => setQuestionType('open_dialogue')}
            />
            <RadioOption
              value="rating_scale"
              label="Rating Scale"
              checked={questionType === 'rating_scale'}
              onChange={() => setQuestionType('rating_scale')}
            />
          </div>
        </div>

        {showTeams && (
          <TeamsApplied
            teams={teams}
            selectedTeamIds={selectedTeamIds}
            onToggle={toggleTeam}
          />
        )}

        {/* Footer */}
        <div className="flex justify-between items-center">
          <a
            href={`/orgs/${orgSlug}/custom-rating-scales`}
            className="inline-flex items-center gap-0.5 font-talent text-base font-normal text-green-700 hover:text-green-800 leading-5"
          >
            Go to Custom Rating Scales
            <ArrowSquareOut className="h-4 w-4" />
          </a>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-3.5 py-2 rounded-sm font-talent text-base font-medium text-gray-500 hover:text-gray-700 leading-5"
            >
              Cancel
            </button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="!bg-green-600 hover:!bg-green-700 !border-green-600 leading-5 font-medium text-base"
            >
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
